/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-12 10:27:50
 *
 *******************************************************/
//@flow
import moment from 'moment';
import React, { Component } from 'react';
import { Button, Popover, Stack, FormLayout, DatePicker, TextField } from '@shopify/polaris';
import { CalendarMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';

import { getStoreDate, displayDateRange } from 'src/helpers/date-time-helper';
import { DATE_FORMAT } from 'src/constants/language-setting';

type Props = {
  enabledFromDate: Date | string,
  enabledToDate: Date | string,
  appLanguage: string,
  onChooseDate: Function,
  popoverActive: boolean,
  togglePopoverDateActive: Function
};

type ComposedProps = Props & WithI18nProps;

type State = {
  startDate: string,
  endDate: string,
  defaultStartDate: Date | string,
  defaultEndDate: Date | string,
  month: number,
  year: number,
  clear: boolean,
  selectedDates: {
    start: Date,
    end: Date
  }
};
// DONE
class RenderDatePickerComponent extends Component<ComposedProps, State> {
  today = getStoreDate(new Date());
  state = {
    // Date changed from input
    startDate: '',
    endDate: '',
    // Date passed from form
    defaultStartDate: '',
    defaultEndDate: '',

    // Date picker
    month: this.today.getMonth(),
    year: this.today.getFullYear(),

    clear: false,
    selectedDates: {
      start: this.today,
      end: this.today
    }
  };

  componentDidMount() {
    const { enabledFromDate, enabledToDate } = this.props;
    this.initialValue(enabledFromDate, enabledToDate);
  }

  componentWillUpdate(nextProps, nextState) {
    const { enabledFromDate, enabledToDate } = nextProps;
    const { enabledFromDate: currentEnabledFromDate, enabledToDate: currentEnabledToDate } =
      this.props;

    if (currentEnabledFromDate === enabledFromDate && currentEnabledToDate === enabledToDate) {
      return;
    }

    this.initialValue(enabledFromDate, enabledToDate);
  }

  render() {
    const {
      month,
      year,
      selectedDates,
      startDate,
      endDate,
      defaultStartDate,
      defaultEndDate,
      clear
    } = this.state;

    const { disabled, i18n, popoverActive, label, placeholder, stylePlaceholder } = this.props;

    const activator = (
      <div className={`date-select subdued-icon`}>
        <Button
          disabled={disabled}
          onClick={this.togglePopoverActive}
          color="base"
          icon={CalendarMajor}
          fullWidth
          textAlign="left">
          {!defaultStartDate && !defaultEndDate ? (
            <div className={stylePlaceholder}>{placeholder}</div>
          ) : (
            displayDateRange(defaultStartDate, defaultEndDate)
          )}
        </Button>
      </div>
    );

    return (
      <div className="datePicker">
        <RenderLabelTooltip label={label} />
        <Popover active={popoverActive} activator={activator} onClose={this.closePopover} sectioned>
          <Popover.Pane>
            <Popover.Section>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.MerchandisingV2.FiltersBar.dateFilter_Start_Label'
                    )}
                    value={startDate}
                    onChange={this.handleChangeStarting}
                    onBlur={this.handleBlurStarting}
                  />
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.MerchandisingV2.FiltersBar.dateFilter_End_Label'
                    )}
                    value={endDate}
                    onChange={this.handleChangeEnding}
                    onBlur={this.handleBlurEnding}
                  />
                </FormLayout.Group>
                <DatePicker
                  month={month}
                  year={year}
                  onChange={this.setSelectedDates}
                  onMonthChange={this.handleMonthChange}
                  selected={clear ? null : selectedDates}
                  multiMonth={true}
                  allowRange={true}
                />
              </FormLayout>
            </Popover.Section>
          </Popover.Pane>
          <Popover.Pane fixed>
            <Popover.Section>
              <div className="BC-Popover__Button BC-Popover__Button_spaceBetween">
                <Stack>
                  <div style={{ marginTop: '0.5rem' }}>
                    <Button plain onClick={this.handleClear}>
                      {i18n.translate('AdminHTML.Common.Action.clearDate')}
                    </Button>
                  </div>
                </Stack>
                <Stack>
                  <Button onClick={this.closePopover}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                  <Button primary onClick={this.handleApply}>
                    {i18n.translate('AdminHTML.Common.Action.apply')}
                  </Button>
                </Stack>
              </div>
            </Popover.Section>
          </Popover.Pane>
        </Popover>
      </div>
    );
  }

  // Toggle state to show/hide the popover
  togglePopoverActive = () => {
    this.setState({ clear: false });
    this.props.togglePopoverDateActive();
  };

  // initial value for date in popover
  initialValue = (enabledFromDate, enabledToDate) => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    // Initial value
    const today = getStoreDate(new Date());
    if (
      (enabledFromDate === undefined || !moment(enabledFromDate).isValid()) &&
      (enabledToDate === undefined || !moment(enabledToDate).isValid())
    ) {
      // the from date and to date are not valid, set today is start/end date
      const startDate = moment(today).format(formatDateNumber);
      const endDate = moment(today).format(formatDateNumber);
      const selectedDates = {
        start: today,
        end: today
      };

      this.setState({
        startDate,
        endDate,
        defaultStartDate: undefined,
        defaultEndDate: undefined,
        selectedDates
      });
    } else {
      // one of or both from date and to date are valid
      let startDate = '';
      let endDate = '';
      let selectedDates = {};

      if (enabledFromDate === undefined || !moment(enabledFromDate).isValid()) {
        // from date is not valid, set start date is today/to date
        const today = getStoreDate(new Date());
        if (moment(enabledToDate).isAfter(today)) {
          startDate = moment(today).format(formatDateNumber);
          selectedDates.start = today;
        } else {
          startDate = moment(enabledToDate).format(formatDateNumber);
          selectedDates.start = moment(enabledToDate).toDate();
        }
      } else {
        // from date is valid, set start date is from date
        startDate = moment(enabledFromDate).format(formatDateNumber);
        selectedDates.start = moment(enabledFromDate).toDate();
      }

      if (enabledToDate === undefined || !moment(enabledToDate).isValid()) {
        // to date is not valid, set end date is today/from date
        const today = getStoreDate(new Date());
        if (moment(enabledFromDate).isAfter(today)) {
          endDate = moment(enabledFromDate).format(formatDateNumber);
          selectedDates.end = moment(enabledFromDate).toDate();
        } else {
          endDate = moment(today).format(formatDateNumber);
          selectedDates.end = today;
        }
      } else {
        // to date is valid, set end date is to date
        endDate = moment(enabledToDate).format(formatDateNumber);
        selectedDates.end = moment(enabledToDate).toDate();
      }

      this.setState({
        startDate,
        endDate,
        defaultStartDate: selectedDates.start,
        defaultEndDate: selectedDates.end,
        selectedDates
      });
    }
  };

  // Handle cancel/close popover
  closePopover = e => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    const { defaultStartDate, defaultEndDate } = this.state;

    //reset value
    this.initialValue(defaultStartDate, defaultEndDate);

    this.togglePopoverActive();
  };

  // Handle change custom date
  setSelectedDates = selectedDates => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const startDate = moment(selectedDates.start).format(formatDateNumber);
    const endDate = moment(selectedDates.end).format(formatDateNumber);

    this.setState({ selectedDates, startDate, endDate, clear: false });
  };

  // Handle change month
  handleMonthChange = (month, year) => {
    this.setState({ month, year });
  };

  // Handle start date input data
  handleChangeStarting = value => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    let { selectedDates } = this.state;
    selectedDates.start = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());

    this.setState({ startDate: value, selectedDates });
  };

  // Handle blur start date input data
  handleBlurStarting = () => {
    // check if the start date is not valid, change to today/end date
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const {
      startDate,
      selectedDates: { start, end }
    } = this.state;

    if (!moment(startDate, formatDateNumber).isValid()) {
      const today = getStoreDate(getStoreDate(new Date()));
      if (moment(end).isAfter(today)) {
        this.handleChangeStarting(moment(today).format(formatDateNumber));
      } else {
        this.handleChangeStarting(moment(end).format(formatDateNumber));
      }
    } else {
      if (moment(start).isAfter(moment(end))) {
        this.handleChangeStarting(moment(end).format(formatDateNumber));
      }
    }
  };

  // Handle end date input data
  handleChangeEnding = value => {
    const { appLanguage } = this.props;
    let { selectedDates } = this.state;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    selectedDates.end = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());

    this.setState({ endDate: value, selectedDates });
  };

  // Handle blur end date input data
  handleBlurEnding = () => {
    // check if the end date is not valid, change to today/start date
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const {
      endDate,
      selectedDates: { start, end }
    } = this.state;

    if (!moment(endDate, formatDateNumber).isValid()) {
      const today = getStoreDate(new Date());
      if (moment(start).isAfter(today)) {
        this.handleChangeEnding(moment(start).format(formatDateNumber));
      } else {
        this.handleChangeEnding(moment(today).format(formatDateNumber));
      }
    } else {
      if (moment(start).isAfter(moment(end))) {
        this.handleChangeEnding(moment(start).format(formatDateNumber));
      }
    }
  };

  // Handle clear data
  handleClear = e => {
    e.stopPropagation();

    //reset value
    // this.initialValue('', '');
    this.setState({
      startDate: '',
      endDate: '',
      clear: true,
      selectedDates: {
        start: this.today,
        end: this.today
      }
    });
  };

  // Handle apply button
  handleApply = e => {
    e.stopPropagation();

    const { onChooseDate } = this.props;
    const { startDate, endDate, selectedDates } = this.state;

    if (startDate === '' && endDate === '') {
      onChooseDate({ start: '', end: '' });
    } else {
      onChooseDate(selectedDates);
    }

    this.togglePopoverActive();
  };
}

export default withI18n()(RenderDatePickerComponent);
